type NetworkErrorLike = {
  message?: string
  name?: string
  statusCode?: number
  response?: {
    status?: number
  }
}

type ApolloNetworkErrorLike = {
  clientErrors?: unknown[]
  graphQLErrors?: unknown[]
  networkError?: NetworkErrorLike | null
}

const networkFailureMessages = [
  /load failed/i,
  /failed to fetch/i,
  /networkerror when attempting to fetch resource/i,
]

const hasItems = (value: unknown) => Array.isArray(value) && value.length > 0

const getStatusCode = (error: NetworkErrorLike) =>
  error.statusCode ?? error.response?.status

export const isKnownNetworkFailureMessage = (message = '') =>
  networkFailureMessages.some(pattern => pattern.test(message))

export const isIgnorableConnectionProblem = (error: unknown) => {
  if (!error || typeof error !== 'object') return false

  const networkError = error as NetworkErrorLike
  const statusCode = getStatusCode(networkError)
  const name = networkError.name || ''
  const message = networkError.message || ''

  if (statusCode) return false

  return (
    name === 'AbortError' ||
    isKnownNetworkFailureMessage(message) ||
    name === 'TypeError'
  )
}

/**
 * Determines if a given Apollo network error is ignorable
 * depending if it has graphQLErrors or clientErrors, and if the networkError is an ignorable connection problem.
 * @param error
 * @returns boolean - true if the error is an ignorable Apollo network error, false otherwise
 */
export const isIgnorableApolloNetworkError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false

  const apolloError = error as ApolloNetworkErrorLike

  if (!apolloError.networkError) return false
  if (hasItems(apolloError.graphQLErrors)) return false
  if (hasItems(apolloError.clientErrors)) return false

  return isIgnorableConnectionProblem(apolloError.networkError)
}
